import { NotificationsActiveOutlined, NotificationsNoneOutlined } from '@mui/icons-material'
import { CircularProgress, IconButton, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFollowEntity, useGetSubscriptionStatus, useUnfollowEntity } from 'src/api/subscription'

interface FollowButtonProps {
  customerId: EntityId
  entityType: string
  entityId: EntityId
  size?: 'small' | 'medium'
}

/**
 * Reusable bell icon button (with tooltip) to follow/unfollow a record's notifications.
 * Place on any entity detail header (deviation, work order, equipment).
 */
export const FollowButton: React.FC<FollowButtonProps> = ({ customerId, entityType, entityId, size = 'medium' }) => {
  const { t } = useTranslation('common')
  const { data: status, isLoading } = useGetSubscriptionStatus(customerId, entityType, entityId)
  const { mutateAsync: follow, isLoading: isFollowing } = useFollowEntity(customerId)
  const { mutateAsync: unfollow, isLoading: isUnfollowing } = useUnfollowEntity(customerId)

  const following = !!status?.isFollowing
  const busy = isFollowing || isUnfollowing

  const onClick = async () => {
    if (following) {
      await unfollow({ entityType, entityId })
    } else {
      await follow({ entityType, entityId })
    }
  }

  return (
    <Tooltip title={(following ? t('following') : t('follow')) as string}>
      <span>
        <IconButton
          size={size}
          onClick={onClick}
          disabled={busy || isLoading}
          color={following ? 'primary' : 'default'}
        >
          {busy ? (
            <CircularProgress size={18} color="inherit" />
          ) : following ? (
            <NotificationsActiveOutlined />
          ) : (
            <NotificationsNoneOutlined />
          )}
        </IconButton>
      </span>
    </Tooltip>
  )
}

export default FollowButton
